import { getFitnessDemonstrationPhase } from "@/lib/fitnessDemonstrationPhase";
import { getFitnessWalkingResetCopy } from "@/lib/fitnessWalkingDemonstrationCopy";
import { getExplicitFitnessMovement } from "@/lib/fitnessExerciseIdentity";
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { BookOpen, Dumbbell, Pause, Play, RotateCcw, RefreshCw } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { FALLBACK_LANG } from "@/i18n/translations";
import { getExerciseAvatarDemonstration } from "@/lib/fitnessDemonstration";
import { getExerciseGuidance } from "@/lib/fitnessExerciseGuidance";
import { getFitnessInstructor, type FitnessInstructorId } from "@/lib/fitnessInstructor";
import { fitnessAssetTimeoutMs } from "@/lib/fitnessAssetLoadPolicy";
import type { ExerciseAvatarProgress } from "@/components/fitness/ExerciseAvatarDemonstration";
import type { ExerciseMediaSources } from "@/hooks/useExerciseMedia";

const AvatarDemonstration = lazy(() => import("@/components/fitness/ExerciseAvatarDemonstration"));

class AvatarBoundary extends React.Component<{ children: React.ReactNode; onError: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onError(); }
  render() { return this.state.failed ? null : this.props.children; }
}

interface ExerciseDemonstrationProps {
  exerciseId?: string;
  exerciseName: string;
  instructorId?: FitnessInstructorId;
  /** Kept for existing callers. Fitness presents only its own 3D demonstrations. */
  media?: Partial<ExerciseMediaSources>;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  paused?: boolean;
  avatarPlaybackRequested?: boolean;
  instructions?: string[];
  className?: string;
  variant?: "standard" | "stage";
}

// Changing instructors invalidates old renderer callbacks, without touching the personal avatar.
export default function ExerciseDemonstration(props: ExerciseDemonstrationProps) {
  const identity = JSON.stringify([props.exerciseId, props.exerciseName, props.instructorId]);
  return <DemonstrationPlayer key={identity} {...props} />;
}

function DemonstrationPlayer({ exerciseId, exerciseName, instructorId, loading = false, error, onRetry,
  paused = false, avatarPlaybackRequested, instructions = [], className = "", variant = "standard" }: ExerciseDemonstrationProps) {
  const { t, tf, lang } = useLanguage();
  const explicit = getExplicitFitnessMovement(exerciseId || "");
  const guidance = explicit ? getExerciseGuidance(explicit.canonicalName, lang)
    : getExerciseGuidance(exerciseId || "", lang) ?? getExerciseGuidance(exerciseName, lang);
  const displayInstructions = guidance?.steps ?? instructions;
  const avatar = explicit ? getExerciseAvatarDemonstration(explicit.canonicalName)
    : getExerciseAvatarDemonstration(exerciseId || "") ?? getExerciseAvatarDemonstration(exerciseName);
  const [avatarFailed, setAvatarFailed] = useState(false), [avatarReady, setAvatarReady] = useState(false);
  const [view, setView] = useState<"front" | "side">("front");
  const [cameraResetKey, setCameraResetKey] = useState(0);
  const [playing, setPlaying] = useState(false), [speed, setSpeed] = useState(1), [restart, setRestart] = useState(0);
  const avatarRun = useRef(0), seekId = useRef(0), live = useRef(true);
  const [avatarProgress, setAvatarProgress] = useState<ExerciseAvatarProgress>({ currentTime: 0, duration: 0 });
  const [avatarSeek, setAvatarSeek] = useState<{ id: number; time: number } | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [hidden, setHidden] = useState(() => typeof document !== "undefined" && document.hidden);
  const playbackHeld = useRef(hidden);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
  const hasDefinition = !!avatar;
  const hasAvatar = hasDefinition && !avatarFailed;
  const blocked = paused || hidden;
  const buttonClass = "min-h-11 min-w-11 rounded-xl border border-border bg-background/80 px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40";

  useEffect(() => {
    live.current = true;
    const visibility = () => {
      if (document.hidden) playbackHeld.current = true;
      setHidden(document.hidden);
    };
    document.addEventListener("visibilitychange", visibility);
    return () => { live.current = false; document.removeEventListener("visibilitychange", visibility); };
  }, []);
  useEffect(() => { if (blocked) setPlaying(false); }, [blocked]);
  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!query) return;
    const changed = () => {
      setReducedMotion(query.matches);
      if (query.matches) { playbackHeld.current = true; setPlaying(false); }
    };
    query.addEventListener("change", changed);
    return () => query.removeEventListener("change", changed);
  }, []);
  useEffect(() => {
    if (avatarPlaybackRequested === undefined || !hasAvatar) return;
    setPlaying(avatarPlaybackRequested && avatarReady && !blocked && !playbackHeld.current && !reducedMotion);
  }, [avatarPlaybackRequested, avatarReady, blocked, hasAvatar, reducedMotion]);
  useEffect(() => {
    setTimedOut(false);
    if (!((!hasDefinition && loading) || (hasAvatar && !avatarReady))) return;
    const timeout = window.setTimeout(() => {
      setTimedOut(true); setPlaying(false);
      if (hasAvatar && !avatarReady) setAvatarFailed(true);
    }, hasAvatar ? fitnessAssetTimeoutMs(getFitnessInstructor(instructorId).modelUrl) + 1000 : 15000);
    return () => window.clearTimeout(timeout);
  }, [loading, hasDefinition, hasAvatar, avatarReady, instructorId]);

  const avatarError = useCallback(() => {
    if (!live.current || avatarRun.current !== restart) return;
    setAvatarFailed(true); setPlaying(false);
  }, [restart]);
  const avatarTime = useCallback((progress: ExerciseAvatarProgress) => {
    if (!live.current || avatarRun.current !== restart || !Number.isFinite(progress.duration) || progress.duration <= 0) return;
    setAvatarProgress({ duration: progress.duration, currentTime: Number.isFinite(progress.currentTime) ? Math.max(0, Math.min(progress.currentTime, progress.duration)) : 0 });
  }, [restart]);
  const avatarLoaded = useCallback((progress: ExerciseAvatarProgress) => {
    if (!live.current || avatarRun.current !== restart) return;
    avatarTime(progress); setAvatarReady(true);
  }, [avatarTime, restart]);
  const seekAvatar = (requestedTime: number) => {
    if (blocked || !avatarReady || avatarProgress.duration <= 0) return;
    playbackHeld.current = true;
    const time = Number.isFinite(requestedTime) ? Math.max(0, Math.min(requestedTime, avatarProgress.duration)) : 0;
    setPlaying(false); setAvatarProgress({ ...avatarProgress, currentTime: time });
    setAvatarSeek({ id: ++seekId.current, time });
  };
  const toggle = () => {
    if (blocked || !avatarReady) return;
    playbackHeld.current = playing;
    setPlaying(value => !value);
  };
  const retry = () => {
    playbackHeld.current = false;
    setAvatarFailed(false); setAvatarReady(false); setPlaying(false); setTimedOut(false);
    avatarRun.current++;
    setAvatarProgress({ currentTime: 0, duration: 0 }); setAvatarSeek(null);
    setRestart(avatarRun.current); onRetry?.();
  };
  const activeLoading = ((!avatar && loading) || (hasAvatar && !avatarReady)) && !timedOut;
  const unavailable = !hasAvatar && !activeLoading;
  const demonstrationPhase = hasAvatar && avatarReady && avatar
    ? getFitnessDemonstrationPhase(avatar.id, avatarProgress.currentTime, avatarProgress.duration) : null;
  const phase21 = demonstrationPhase?.protocol === "21s" ? demonstrationPhase : null;
  const walkingReset = demonstrationPhase?.protocol === "walking-lunges" && demonstrationPhase.kind === "reset"
    ? getFitnessWalkingResetCopy(lang) : null;

  const playbackControls = <>
        <button type="button" onClick={toggle} disabled={blocked || !avatarReady} className={buttonClass} aria-label={t(playing ? "fitness.demo.pause" : "fitness.demo.play")}>
          {playing ? <Pause size={18} aria-hidden="true" /> : <Play size={18} aria-hidden="true" />}
        </button>
        <button type="button" onClick={() => seekAvatar(0)} disabled={blocked || !avatarReady} className={buttonClass} aria-label={t("fitness.demo.restart")}><RotateCcw size={18} aria-hidden="true" /></button>
  </>;

  return (
    <section aria-label={t("fitness.demo.title")} className={`overflow-hidden rounded-2xl border border-border bg-card text-foreground ${className}`}>
      <header className={`flex flex-wrap items-center justify-between gap-x-3 px-4 py-3 ${variant === "stage" ? "gap-y-1" : "gap-y-3"}`}>
        <p className="min-w-0 max-w-full break-words text-xs font-display font-semibold uppercase tracking-wider text-primary">{t(hasAvatar ? "fitness.demo.avatar" : "fitness.demo.title")}</p>
        {variant === "stage" && hasAvatar && <div className="flex shrink-0 gap-2">{playbackControls}</div>}
        <p className={`min-w-0 max-w-full break-words text-xs text-muted-foreground ${variant === "stage" ? "w-full" : ""}`}>{t("fitness.demo.previewOnly")}</p>
      </header>
      <div data-fitness-demonstration-viewport className={`relative flex w-full items-center justify-center overflow-hidden bg-background ${variant === "stage" ? "h-[var(--fitness-stage-height,min(44svh,440px))] min-h-[180px] max-h-[440px] lg:h-[min(66svh,640px)] lg:min-h-[420px] lg:max-h-[640px]" : "min-h-[280px] aspect-[4/3] max-h-[420px]"}`}>
        {hasAvatar ? <AvatarBoundary key={restart} onError={avatarError}>
          <Suspense fallback={null}>
            <AvatarDemonstration exerciseId={avatar!.id} instructorId={instructorId} paused={!playing || blocked}
              playbackRate={speed} view={view} cameraResetKey={cameraResetKey} label={exerciseName} className="h-full w-full"
              seekRequest={avatarSeek} onReady={avatarLoaded} onTimeUpdate={avatarTime} onError={avatarError} />
          </Suspense>
        </AvatarBoundary> : <div className="flex max-w-sm flex-col items-center gap-3 px-6 py-8 text-center">
          <Dumbbell size={40} aria-hidden="true" className="text-primary/70" />
          <p role="status" className="text-sm leading-relaxed text-muted-foreground">
            {activeLoading ? t("fitness.demo.loading") : t(error || avatarFailed || timedOut ? "fitness.demo.failed" : "fitness.demo.noMedia")}
          </p>
        </div>}
        {activeLoading && hasAvatar && <p role="status" className="pointer-events-none absolute left-3 top-3 rounded-lg bg-background/90 px-3 py-2 text-xs">{t("fitness.demo.loading")}</p>}
      </div>
      {phase21 && <div role="group" aria-label={t("fitness.demo21.title")} className="min-h-24 space-y-1 border-t border-border bg-primary/5 px-4 py-3">
        <p className="text-xs font-medium text-muted-foreground">{t("fitness.demo21.title")}</p>
        <p aria-live="polite" aria-atomic="true" className="text-sm font-semibold text-foreground">{t("fitness.demo21.phase." + phase21.kind)}</p>
        {phase21.demonstrationRep !== null && <p className="text-xs text-muted-foreground">{tf("fitness.demo21.movement", { current: String(phase21.demonstrationRep), total: String(phase21.repetitionsInBlock) })}</p>}
      </div>}
      {walkingReset && <div role="group" aria-label={walkingReset.cue} className="min-h-24 space-y-1 border-t border-border bg-primary/5 px-4 py-3">
        <p aria-live="polite" aria-atomic="true" className="text-sm font-semibold text-foreground">{walkingReset.cue}</p>
        <p className="text-xs text-muted-foreground">{walkingReset.notRepetitionLabel}</p>
      </div>}
      {hasAvatar && <div className="flex flex-wrap items-center gap-2 border-t border-border px-3 py-3">
        {variant === "standard" && playbackControls}
        <div className="flex gap-1">
          {(["front", "side"] as const).map(angle => <button key={angle} type="button" className={buttonClass} aria-pressed={view === angle} onClick={() => { setView(angle); setCameraResetKey(key => key + 1); }}>{t("fitness.demo." + angle)}</button>)}
        </div>
        <label className="ml-auto flex min-h-11 items-center gap-2 text-xs text-muted-foreground">
          {t("fitness.demo.speed")}
          <select aria-label={t("fitness.demo.speed")} value={speed} onChange={event => setSpeed(Number(event.target.value))} className="min-h-11 rounded-xl border border-border bg-background px-3 text-foreground">
            {[0.5, 0.75, 1].map(value => <option key={value} value={value}>{value}×</option>)}
          </select>
        </label>
      </div>}
      {hasAvatar && avatarReady && avatarProgress.duration > 0 && <label className="flex min-w-0 flex-col gap-1 border-t border-border px-4 py-3 text-xs text-muted-foreground">
        <span>{t("fitness.demo.position")}</span>
        <input type="range" min={0} max={avatarProgress.duration} step="any" value={avatarProgress.currentTime}
          aria-label={t("fitness.demo.position")} aria-valuetext={`${Math.round(avatarProgress.currentTime / avatarProgress.duration * 100)}%`}
          disabled={blocked} onChange={event => seekAvatar(Number(event.target.value))}
          className="min-h-11 w-full min-w-0 cursor-pointer accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40" />
      </label>}
      {(unavailable || timedOut || avatarFailed) && (onRetry || avatar) && <div className="border-t border-border px-4 py-3">
        <button type="button" onClick={retry} className={`${buttonClass} inline-flex items-center gap-2`}><RefreshCw size={16} aria-hidden="true" />{t("fitness.demo.retry")}</button>
      </div>}
      {displayInstructions.length > 0 && <details className="border-t border-border px-4 py-3" open={unavailable || undefined}>
        <summary className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"><BookOpen size={17} aria-hidden="true" />{t(guidance || lang === FALLBACK_LANG ? "fitness.demo.steps" : "fitness.demo.originalSteps")}</summary>
        <ol className="ml-5 list-decimal space-y-3 pb-2 pt-2 text-sm leading-relaxed text-muted-foreground">{displayInstructions.map((step, index) => <li key={index} className="pl-1">{step}</li>)}</ol>
      </details>}
    </section>
  );
}
